import { useContext, useState, useCallback, Fragment, useMemo } from "react";
import ReactDataGrid from "react-data-grid";
import { Localization, SessionContext, AppMessagesContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { TooltipWrapper } from "src/propertyInfoTemplates/PropertyComponents";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { AnnotationContextType, AnnotationType, AnnotationQuality, type ANNAnnotationRecord } from "./ANNTypes";
import { useAnnotations, useDeleteAnnotation } from "./ANNState";
import { ANNCreateNote } from "./ANNCreateNote";
import { ANNCreateDocument } from "./ANNCreateDocument";
import { ANNAnnotationActionsCell, ANNAnnotationPrimaryActionCell } from "./ANNAnnotationActionsCell";
import { ANNEditorActionType, useANNEditorContext } from "./ANNEditorState";
import { ManagedWindow, closeWindow, openWindow, minimizeWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { MapitStateContext, actionAddDataLayer } from "src/states/MapitState";
import { LayerFunc } from "src/managers/LayerFunc";
import { LayerInfo, LayerType, LayerVariabilityType } from "src/common/managers/Types";
import { blobToDataUrl, downloadOverlayImage, extractS3KeyFromUrl } from "./ANNImageOverlayStorage";
import type { Geometry } from "geojson";
import { featureCollection, feature } from "@turf/turf";
import { GlassButton } from "src/components/GlassButtons";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { buildServitutDetailsParams } from "./ANNServitutIdentity";

interface ANNControlPanelProps {
   managedWindow: ManagedWindow;
}

export function ANNControlPanel(props: ANNControlPanelProps) {
   const { state: sessionState } = useContext(SessionContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { dispatch: mapitStateDispatch, state: mapitState } = useContext(MapitStateContext);
   const { dispatch: editorDispatch } = useANNEditorContext();
   const [activeTab, setActiveTab] = useState<"ejendom" | "servitut">("servitut");
   const [importingId, setImportingId] = useState<string | null>(null);
   const [downloadingId, setDownloadingId] = useState<string | null>(null);
   const [deletingId, setDeletingId] = useState<string | null>(null);
   const [editingId, setEditingId] = useState<string | null>(null);
   const [showOnlyUnverified, setShowOnlyUnverified] = useState(false);
   const [datoloebenrSearch, setDatoloebenrSearch] = useState<string>("");
   const [bfeSearch, setBfeSearch] = useState<string>("");
   const [editNoteModalOpen, setEditNoteModalOpen] = useState(false);
   const [editDocumentModalOpen, setEditDocumentModalOpen] = useState(false);
   const [annotationToEdit, setAnnotationToEdit] = useState<ANNAnnotationRecord | null>(null);
   const [documentToEdit, setDocumentToEdit] = useState<ANNAnnotationRecord | null>(null);
   const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(new Set());
   const [selectedFilterLayerId, setSelectedFilterLayerId] = useState<number | "">("");
   const [filterGeometry, setFilterGeometry] = useState<Geometry | undefined>(undefined);
   const organizationId = sessionState?.customerRef;
   const deleteAnnotation = useDeleteAnnotation();
   const canDelete = hasAccessToFeature(Feature.AnnotationDelete);
   const canWrite = hasAccessToFeature(Feature.AnnotationWrite);
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);

   const availableFilterLayers = useMemo(() => {
      return Object.values(mapitState.layers || {})
         .filter(layer => layer.layerVariability !== LayerVariabilityType.FixedFeatureLayer)
         .map(layer => {
            const hasGeometry = layer.geoJson?.features?.length > 0 || (layer.imageOverlay?.coordinates && layer.imageOverlay.coordinates?.length > 0);
            if (!hasGeometry) return null;
            const typeLabel = layer.type === LayerType.ImageOverlay ? "Image" :
               layer.type === LayerType.GeoJSON_Line ? "Line" :
                  layer.type === LayerType.GeoJSON_Polygon ? "Polygon" :
                     layer.type === LayerType.GeoJSON_Point ? "Point" : "Other";
            return { layer, typeLabel };
         })
         .filter((item): item is { layer: LayerInfo; typeLabel: string } => item !== null);
   }, [mapitState.layers]);

   const extractGeometryFromLayer = useCallback((layer: LayerInfo): Geometry | undefined => {
      const geometries: Geometry[] = [];

      if (layer.geoJson?.features) {
         for (const feature of layer.geoJson.features) {
            if (feature.geometry) {
               geometries.push(feature.geometry);
            }
         }
      }

      if (layer.imageOverlay?.coordinates && layer.imageOverlay.coordinates.length > 0) {
         const polygon: Geometry = {
            type: "Polygon",
            coordinates: [layer.imageOverlay.coordinates]
         };
         geometries.push(polygon);
      }

      if (geometries.length === 0) {
         return undefined;
      } else if (geometries.length === 1) {
         return geometries[0];
      } else {
         const geometryTypes = new Set(geometries.map(g => g.type));
         if (geometryTypes.size === 1) {
            const type = geometries[0].type;
            if (type === "Polygon") {
               return {
                  type: "MultiPolygon",
                  coordinates: geometries.map(g => (g as any).coordinates)
               };
            } else if (type === "LineString") {
               return {
                  type: "MultiLineString",
                  coordinates: geometries.map(g => (g as any).coordinates)
               };
            } else if (type === "Point") {
               return {
                  type: "MultiPoint",
                  coordinates: geometries.map(g => (g as any).coordinates)
               };
            }
         }
         return {
            type: "GeometryCollection",
            geometries: geometries
         };
      }
   }, []);

   const ejendomScope = useAnnotations({
      kind: "all",
      contextType: AnnotationContextType.Ejendom,
      organizationId,
      filterGeometry,
   });
   const servitutScope = useAnnotations({
      kind: "all",
      contextType: AnnotationContextType.Servitut,
      organizationId,
      filterGeometry,
   });

   const handleApplyGeometryFilter = useCallback(async () => {
      if (selectedFilterLayerId === "" || selectedFilterLayerId === null || selectedFilterLayerId === undefined) {
         setFilterGeometry(undefined);
         return;
      }
      const layerId = typeof selectedFilterLayerId === "number" ? selectedFilterLayerId : parseInt(String(selectedFilterLayerId));
      if (isNaN(layerId)) return;

      const layer = availableFilterLayers.find(l => l.layer.layerId === layerId);
      if (!layer) return;

      const geometry = extractGeometryFromLayer(layer.layer);
      if (geometry) {
         setFilterGeometry(geometry);
      } else {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Selected layer has no geometry")));
      }
   }, [selectedFilterLayerId, availableFilterLayers, extractGeometryFromLayer, appMessageDispatch]);

   const handleClearGeometryFilter = useCallback(() => {
      setSelectedFilterLayerId("");
      setFilterGeometry(undefined);
   }, []);

   const handleRefresh = useCallback(() => {
      void (activeTab === "ejendom" ? ejendomScope.refresh() : servitutScope.refresh());
   }, [activeTab, ejendomScope, servitutScope]);

   const handleOpenServitutDetails = useCallback((annotation: ANNAnnotationRecord) => {
      if (!annotation.documentId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No document ID available")));
         return;
      }
      windowDispatch(openWindow(WindowId.ServitutDetails, buildServitutDetailsParams(annotation)));
   }, [windowDispatch, appMessageDispatch]);

   const handleImport = useCallback(
      async (record: ANNAnnotationRecord) => {
         if (record.type !== AnnotationType.Stedfaestelse) {
            return;
         }
         const layers = record.layers || [];
         const layersToImport = layers.filter(
            (layer) => {
               if (layer.geoJson?.features?.length) {
                  return true;
               }
               if (layer.type === LayerType.ImageOverlay && layer.imageOverlay?.coordinates?.length) {
                  return true;
               }
               return false;
            }
         );
         if (!layersToImport.length) {
            appMessageDispatch(
               actionSetErrorMessage(
                  Localization.getText("No overlays or geometries available")
               )
            );
            return;
         }
         setImportingId(record.id);
         try {
            const geometriesForZoom: Geometry[] = [];

            for (const layerSnapshot of layersToImport) {
               if (layerSnapshot.geoJson?.features?.length) {
                  const isImageOverlay = layerSnapshot.type === LayerType.ImageOverlay;
                  if (isImageOverlay) {
                     const firstFeature = layerSnapshot.geoJson.features[0];
                     const imgUrl = firstFeature?.properties?._imgUrl;
                     if (!imgUrl) {
                        continue;
                     }
                     const geometry = firstFeature?.geometry;
                     if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) {
                        console.warn("ImageOverlay layer missing valid polygon geometry");
                        continue;
                     }
                     geometriesForZoom.push(geometry);
                     const coordinates = geometry.type === "Polygon"
                        ? geometry.coordinates[0]
                        : geometry.coordinates[0]?.[0];
                     if (!coordinates || coordinates.length < 4) {
                        continue;
                     }
                     const coords = (coordinates.slice(0, 4) as [number, number][]);
                     let dataUrl = imgUrl;
                     if (imgUrl.startsWith("http") && !imgUrl.startsWith("data:")) {
                        try {
                           const s3Key = extractS3KeyFromUrl(imgUrl);
                           if (s3Key) {
                              const { blob } = await downloadOverlayImage(s3Key);
                              dataUrl = await blobToDataUrl(blob);
                           } else {
                              const response = await fetch(imgUrl);
                              if (!response.ok) {
                                 throw new Error("Unable to fetch overlay image.");
                              }
                              const blob = await response.blob();
                              dataUrl = await blobToDataUrl(blob);
                           }
                        } catch (err) {
                           console.error("Failed to fetch image:", err);
                           continue;
                        }
                     }
                     if (!dataUrl.startsWith("data:")) {
                        console.warn("Image URL was not converted to dataUrl, MapLibre may make duplicate network call:", imgUrl);
                     }
                     const layerName = layerSnapshot.datasetName || layerSnapshot.filename || record.documentId || `annotation-${record.id}`;
                     const newLayer = LayerFunc.createLayerInfoForImageOverlay(
                        layerName,
                        layerSnapshot.filename || layerName,
                        dataUrl,
                        coords
                     );
                     newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                     newLayer.filename = layerSnapshot.filename || newLayer.filename;
                     newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                     newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                     mapitStateDispatch(actionAddDataLayer(newLayer));
                  } else {
                     layerSnapshot.geoJson.features.forEach(f => {
                        if (f.geometry) {
                           geometriesForZoom.push(f.geometry);
                        }
                     });
                     const label = layerSnapshot.datasetName || layerSnapshot.filename || `${record.documentId || "annotation"}-geometry`;
                     let newLayer: LayerInfo;
                     if (layerSnapshot.geoJson?.type === "FeatureCollection") {
                        newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(layerSnapshot.geoJson, label);
                     } else {
                        newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(
                           { type: "FeatureCollection", features: layerSnapshot.geoJson?.features ?? [] } as any,
                           label
                        );
                     }
                     newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                     newLayer.filename = layerSnapshot.filename || newLayer.filename;
                     newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                     newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                     if (layerSnapshot.propertiesInGeoJson) {
                        newLayer.propertiesInGeoJson = [...layerSnapshot.propertiesInGeoJson];
                     }
                     if (layerSnapshot.propertiesToDisplay) {
                        newLayer.propertiesToDisplay = [...layerSnapshot.propertiesToDisplay];
                     }
                     mapitStateDispatch(actionAddDataLayer(newLayer));
                  }
               }
               else if (layerSnapshot.type === LayerType.ImageOverlay && layerSnapshot.imageOverlay?.coordinates?.length) {
                  const polygon: Geometry = {
                     type: "Polygon",
                     coordinates: [layerSnapshot.imageOverlay.coordinates]
                  };
                  geometriesForZoom.push(polygon);
                  const overlay = layerSnapshot.imageOverlay;
                  let dataUrl = overlay.dataUrl;
                  if (!dataUrl || (dataUrl.startsWith("http") && !dataUrl.startsWith("data:"))) {
                     if (overlay.s3Key) {
                        const { blob } = await downloadOverlayImage(overlay.s3Key);
                        dataUrl = await blobToDataUrl(blob);
                     } else if (overlay.s3Url) {
                        const s3Key = extractS3KeyFromUrl(overlay.s3Url);
                        if (s3Key) {
                           const { blob } = await downloadOverlayImage(s3Key);
                           dataUrl = await blobToDataUrl(blob);
                        } else {
                           const response = await fetch(overlay.s3Url);
                           if (!response.ok) {
                              throw new Error("Unable to fetch overlay image.");
                           }
                           const blob = await response.blob();
                           dataUrl = await blobToDataUrl(blob);
                        }
                     } else {
                        continue;
                     }
                  }
                  if (!dataUrl.startsWith("data:")) {
                     console.warn("Image URL was not converted to dataUrl, MapLibre may make duplicate network call:", overlay.s3Url || overlay.dataUrl);
                  }
                  const coords = overlay.coordinates.slice(0, 4) as [number, number][];
                  const layerName = layerSnapshot.datasetName || layerSnapshot.filename || record.documentId || `annotation-${record.id}`;
                  const newLayer = LayerFunc.createLayerInfoForImageOverlay(
                     layerName,
                     layerSnapshot.filename || layerName,
                     dataUrl,
                     coords
                  );
                  newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                  newLayer.filename = layerSnapshot.filename || newLayer.filename;
                  newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                  newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                  mapitStateDispatch(actionAddDataLayer(newLayer));
               }
            }

            if (geometriesForZoom.length > 0 && mapitState.map) {
               setTimeout(() => {
                  const features = geometriesForZoom.map(geom => feature(geom));
                  const fc = featureCollection(features);
                  mapitState.map.zoomToDataAvoidingWindows(fc, 25);
               }, 600);
            }
         } catch (importError: any) {
            appMessageDispatch(actionSetErrorMessage(importError?.message || "Failed to import overlay."));
         } finally {
            setImportingId(null);
         }
      },
      [appMessageDispatch, mapitStateDispatch, mapitState]
   );

   const handleDownloadDocument = useCallback(
      async (record: ANNAnnotationRecord) => {
         if (!record.document?.s3Key) {
            return;
         }
         setDownloadingId(record.id);
         try {
            const { blob, fileName } = await downloadOverlayImage(record.document.s3Key);
            const downloadName = record.document.filename || fileName || `document-${record.id}`;
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = downloadName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
         } catch (err: any) {
            appMessageDispatch(
               actionSetErrorMessage(
                  err?.message || Localization.getText("Failed to download document")
               )
            );
         } finally {
            setDownloadingId(null);
         }
      },
      [appMessageDispatch]
   );
   const handleOpenDocument = useCallback(
      async (record: ANNAnnotationRecord) => {
         if (!record.document?.s3Key) {
            return;
         }
         setDownloadingId(record.id);
         try {
            const { blob } = await downloadOverlayImage(record.document.s3Key);
            const pdfUrl = URL.createObjectURL(blob);
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
               pdfUrl,
               pdfBlob: blob,
               documentId: record.contextType === AnnotationContextType.Servitut ? record.documentId : undefined,
               bfeNumber: record.contextType === AnnotationContextType.Ejendom ? record.bfeNumber : undefined,
               contextType: record.contextType,
               organizationId,
               userId: sessionState?.userRef,
               userName: sessionState?.userName,
               datoloebenr: record.datoloebenr,
               documentFilename: record.document.filename,
            }));
         } catch (err: any) {
            appMessageDispatch(
               actionSetErrorMessage(
                  err?.message || Localization.getText("Failed to open document")
               )
            );
         } finally {
            setDownloadingId(null);
         }
      },
      [appMessageDispatch, windowDispatch, organizationId, sessionState]
   );

   const handleShowNote = useCallback((record: ANNAnnotationRecord) => {
      const titleLabel = Localization.getText("Title");
      const textLabel = Localization.getText("Note text");
      let htmlParts: string[] = [];
      if (record.title) {
         htmlParts.push(`<strong>${titleLabel}:</strong><br/>${record.title.replace(/\n/g, '<br/>')}`);
      }
      if (record.note?.text) {
         htmlParts.push(`<strong>${textLabel}:</strong><br/>${record.note.text.replace(/\n/g, '<br/>')}`);
      }
      if (htmlParts.length === 0) {
         htmlParts.push(Localization.getText("No note text"));
      }
      const htmlMessage = htmlParts.join("<br/><br/>");
      appMessageDispatch(actionSetInfoMessage(htmlMessage));
   }, [appMessageDispatch]);
   const handleToggleHistory = useCallback((record: ANNAnnotationRecord) => {
      setExpandedHistoryIds(prev => {
         const touchedAt = record.lastEditedAt || record.createdAt;
         const entries = [...(record.history || [])].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
         if (entries.length > 0 && touchedAt && entries[0].timestamp === touchedAt) {
            entries.shift();
         }
         if (entries.length === 0) {
            const next = new Set(prev);
            next.delete(record.id);
            return next;
         }

         const next = new Set(prev);
         if (next.has(record.id)) {
            next.delete(record.id);
         } else {
            next.add(record.id);
         }
         return next;
      });
   }, []);

   const handleDelete = useCallback((record: ANNAnnotationRecord) => {
      if (!organizationId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
         return;
      }
      windowDispatch(openWindow(WindowId.ConfirmationDialog, {
         header: Localization.getText("Delete annotation"),
         children: Localization.getText("Are you sure you want to delete this annotation? This action cannot be undone."),
         onSubmit: async () => {
            setDeletingId(record.id);
            try {
               await deleteAnnotation(record.id, organizationId);
            } catch (err: any) {
               appMessageDispatch(actionSetErrorMessage(err?.message || String(err) || Localization.getText("Failed to delete annotation")));
            } finally {
               setDeletingId(null);
            }
         }
      }));
   }, [organizationId, appMessageDispatch, deleteAnnotation, windowDispatch]);

   const handleEditNote = useCallback((record: ANNAnnotationRecord) => {
      if (record.type !== AnnotationType.Note) return;
      setAnnotationToEdit(record);
      setEditNoteModalOpen(true);
   }, []);

   const handleEditDocument = useCallback((record: ANNAnnotationRecord) => {
      if (record.type !== AnnotationType.Document) return;
      setDocumentToEdit(record);
      setEditDocumentModalOpen(true);
   }, []);

   const handleEditStedfaestelse = useCallback(async (record: ANNAnnotationRecord) => {
      if (record.type !== AnnotationType.Stedfaestelse || !organizationId) {
         return;
      }
      setEditingId(record.id);
      try {
         const initialLayerIds = new Set(Object.keys(mapitState.layers || {}).map(id => parseInt(id)));

         const contextType = activeTab === "ejendom" ? AnnotationContextType.Ejendom : AnnotationContextType.Servitut;
         const identifier = contextType === AnnotationContextType.Ejendom
            ? record.bfeNumber
            : record.documentId;

         if (!identifier) {
            appMessageDispatch(actionSetErrorMessage(Localization.getText("No identifier available") || "No identifier available"));
            return;
         }

         windowDispatch(minimizeWindow(WindowId.PropertyInformationDialog));
         windowDispatch(minimizeWindow(WindowId.ServitutDetails));
         windowDispatch(minimizeWindow(WindowId.ANNUnverifiedAnnotations));

         editorDispatch({
            type: ANNEditorActionType.Open,
            payload: { contextType, identifier },
         });

         const layers = record.layers || [];
         const layersToImport = layers.filter(
            (layer) => {
               if (layer.geoJson?.features?.length) {
                  return true;
               }
               if (layer.type === LayerType.ImageOverlay && layer.imageOverlay?.coordinates?.length) {
                  return true;
               }
               return false;
            }
         );

         const geometriesForZoom: Geometry[] = [];

         for (const layerSnapshot of layersToImport) {
            if (layerSnapshot.geoJson?.features?.length) {
               const isImageOverlay = layerSnapshot.type === LayerType.ImageOverlay;
               if (isImageOverlay) {
                  const firstFeature = layerSnapshot.geoJson.features[0];
                  const imgUrl = firstFeature?.properties?._imgUrl;
                  if (!imgUrl) {
                     console.warn("ImageOverlay layer missing _imgUrl in properties");
                     continue;
                  }
                  const geometry = firstFeature?.geometry;
                  if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) {
                     console.warn("ImageOverlay layer missing valid polygon geometry");
                     continue;
                  }
                  geometriesForZoom.push(geometry);
                  const coordinates = geometry.type === "Polygon"
                     ? geometry.coordinates[0]
                     : geometry.coordinates[0]?.[0];
                  if (!coordinates || coordinates.length < 4) {
                     console.warn("ImageOverlay layer missing valid coordinates");
                     continue;
                  }
                  const coords = (coordinates.slice(0, 4) as [number, number][]);
                  let dataUrl = imgUrl;
                  if (imgUrl.startsWith("http") && !imgUrl.startsWith("data:")) {
                     try {
                        const s3Key = extractS3KeyFromUrl(imgUrl);
                        if (s3Key) {
                           const { blob } = await downloadOverlayImage(s3Key);
                           dataUrl = await blobToDataUrl(blob);
                        } else {
                           const response = await fetch(imgUrl);
                           if (!response.ok) {
                              throw new Error("Unable to fetch overlay image.");
                           }
                           const blob = await response.blob();
                           dataUrl = await blobToDataUrl(blob);
                        }
                     } catch (err) {
                        console.error("Failed to fetch image:", err);
                        continue;
                     }
                  }
                  const layerName = layerSnapshot.datasetName || layerSnapshot.filename || record.documentId || `annotation-${record.id}`;
                  const newLayer = LayerFunc.createLayerInfoForImageOverlay(
                     layerName,
                     layerSnapshot.filename || layerName,
                     dataUrl,
                     coords
                  );
                  newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                  newLayer.filename = layerSnapshot.filename || newLayer.filename;
                  newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                  newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                  mapitStateDispatch({
                     ...actionAddDataLayer(newLayer),
                     callbacks: {
                        onLayerAdded: (li: LayerInfo) => {
                           if (typeof li.layerId === "number") {
                              setTimeout(() => {
                                 editorDispatch({ type: ANNEditorActionType.AddLayer, payload: li.layerId as number });
                              }, 0);
                           }
                        }
                     }
                  } as any);
               } else {
                  layerSnapshot.geoJson.features.forEach(f => {
                     if (f.geometry) {
                        geometriesForZoom.push(f.geometry);
                     }
                  });
                  const label = layerSnapshot.datasetName || layerSnapshot.filename || `${record.documentId || "annotation"}-geometry`;
                  let newLayer: LayerInfo;
                  if (layerSnapshot.geoJson?.type === "FeatureCollection") {
                     newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(layerSnapshot.geoJson, label);
                  } else {
                     newLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(
                        { type: "FeatureCollection", features: layerSnapshot.geoJson?.features ?? [] } as any,
                        label
                     );
                  }
                  newLayer.datasetname = layerSnapshot.datasetName || newLayer.datasetname;
                  newLayer.filename = layerSnapshot.filename || newLayer.filename;
                  newLayer.visible = layerSnapshot.visible ?? newLayer.visible;
                  newLayer.styling = { ...newLayer.styling, ...layerSnapshot.styling };
                  mapitStateDispatch({
                     ...actionAddDataLayer(newLayer),
                     callbacks: {
                        onLayerAdded: (li: LayerInfo) => {
                           if (typeof li.layerId === "number") {
                              setTimeout(() => {
                                 editorDispatch({ type: ANNEditorActionType.AddLayer, payload: li.layerId as number });
                              }, 0);
                           }
                        }
                     }
                  } as any);
               }
            } else if (layerSnapshot.type === LayerType.ImageOverlay && layerSnapshot.imageOverlay?.coordinates?.length) {
               const coords = layerSnapshot.imageOverlay.coordinates;
               if (coords.length >= 4) {
                  const polygonGeometry: Geometry = {
                     type: "Polygon",
                     coordinates: [coords]
                  };
                  geometriesForZoom.push(polygonGeometry);
               }
            }
         }

         if (geometriesForZoom.length > 0) {
            setTimeout(() => {
               try {
                  const features = geometriesForZoom.map(geom => feature(geom));
                  const fc = featureCollection(features);
                  mapitState.map.zoomToDataAvoidingWindows(fc, 25);
               } catch  {
               }
            }, 600);
         }

         const titleValue = record.title || record.comment;
         if (titleValue) {
            editorDispatch({
               type: ANNEditorActionType.SetStedfaestelseTitle,
               payload: titleValue,
            });
         }
         editorDispatch({
            type: ANNEditorActionType.SetStedfaestelseHistoryComment,
            payload: "",
         });
         if (record.annotationType) {
            editorDispatch({
               type: ANNEditorActionType.SetStedfaestelseAnnotationType,
               payload: record.annotationType,
            });
         }
         if (record.quality) {
            editorDispatch({
               type: ANNEditorActionType.SetStedfaestelseQuality,
               payload: record.quality,
            });
         }
         editorDispatch({
            type: ANNEditorActionType.SetEditSession,
            payload: {
               oldAnnotationId: record.id,
               datoloebenr: record.datoloebenr,
               originalCreatedAt: record.createdAt,
               originalCreatedBy: record.createdBy,
               originalCreatedByName: record.createdByName,
               initialLayerIds: Array.from(initialLayerIds),
            },
         });

         windowDispatch(openWindow(WindowId.ANNEditor, { contextType, identifier }));
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(err?.message || String(err) || Localization.getText("Failed to start editing")));
      } finally {
         setEditingId(null);
      }
   }, [organizationId, activeTab, mapitStateDispatch, editorDispatch, windowDispatch, appMessageDispatch]);

   const activeScope = activeTab === "ejendom" ? ejendomScope : servitutScope;
   const allAnnotations = activeScope.annotations;
   const loading = activeScope.loading;
   const error = activeScope.error;

   const filteredAnnotations = useMemo(() => {
      let filtered = allAnnotations;

      if (showOnlyUnverified) {
         filtered = filtered.filter(ann => !ann.verified);
      }

      if (activeTab === "servitut" && datoloebenrSearch.trim()) {
         const searchTerm = datoloebenrSearch.trim().toLowerCase();
         filtered = filtered.filter(ann => {
            const datoloebenr = ann.datoloebenr || "";
            return datoloebenr.toLowerCase().includes(searchTerm);
         });
      }
      if (activeTab === "ejendom" && bfeSearch.trim()) {
         const searchTerm = bfeSearch.trim().toLowerCase();
         filtered = filtered.filter(ann => {
            const bfeNumber = ann.bfeNumber ? String(ann.bfeNumber) : "";
            return bfeNumber.toLowerCase().includes(searchTerm);
         });
      }

      return filtered;
   }, [allAnnotations, showOnlyUnverified, datoloebenrSearch, bfeSearch, activeTab]);

   const groupedAnnotations = useMemo(() => {
      const groups: { [key: string]: ANNAnnotationRecord[] } = {};
      filteredAnnotations.forEach(ann => {
         const identifier = activeTab === "ejendom"
            ? String(ann.bfeNumber || "")
            : (ann.documentId || "");
         if (identifier) {
            if (!groups[identifier]) {
               groups[identifier] = [];
            }
            groups[identifier].push(ann);
         }
      });
      return groups;
   }, [filteredAnnotations, activeTab]);

   const baseRows = useMemo(() => {
      const rows: any[] = [];
      Object.entries(groupedAnnotations).forEach(([identifier, annotations]) => {
         annotations.forEach((annotation, index) => {
            const displayQuality = annotation.verifiedQuality || annotation.quality;
            const isVerified = !!annotation.verifiedQuality;
            const technicalType = annotation.type === AnnotationType.Note
               ? Localization.getText("Note")
               : annotation.type === AnnotationType.Document
                  ? Localization.getText("Document")
                  : Localization.getText("Stedfaestelse");

            rows.push({
               _record: annotation,
               identifier: index === 0 ? identifier : "",
               annotationCount: index === 0 ? annotations.length : "",
               type: technicalType,
               annotationType: annotation.annotationType || "-",
               datoloebenr: annotation.datoloebenr || "-",
               bfeNumber: annotation.bfeNumber || "-",
               quality: displayQuality || null,
               createdAt: new Date(annotation.lastEditedAt || annotation.createdAt).toLocaleString(Localization.getLocale()),
               _createdAtSort: new Date(annotation.lastEditedAt || annotation.createdAt).getTime(),
               createdByName: annotation.lastEditedByName || annotation.createdByName || annotation.lastEditedBy || annotation.createdBy,
               title: annotation.title || "-",
               historyComment: ([...(annotation.history || [])].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]?.comment) || "",
               isVerified,
            });
         });
      });
      return rows.sort((a, b) => b._createdAtSort - a._createdAtSort);
   }, [groupedAnnotations]);

   const renderQualityCell = useCallback((quality?: AnnotationQuality, verified?: boolean, verifierInfo?: string) => {
      if (!quality) {
         return <div className="ann-components-quality-cell-empty">-</div>;
      }
      const qualityLabel = quality === AnnotationQuality.Gold
         ? Localization.getText("Gold")
         : quality === AnnotationQuality.Silver
            ? Localization.getText("Silver") || "Silver"
            : Localization.getText("Bronze") || "Bronze";
      const statusText = verified
         ? (verifierInfo || (Localization.getText("Verified") || "Verified"))
         : (Localization.getText("Not verified") || "Not verified");
      const statusTooltip = (
         <div className="ann-tooltip">
            <div className="ann-tooltip__title">{qualityLabel}</div>
            <div className="ann-tooltip__body">{statusText}</div>
         </div>
      );
      return (
         <div
            className="ann-components-quality-cell"
         >
            <div className={`ann-quality-badge ann-quality-${quality.toLowerCase()}`}>
               <span className="ann-quality-badge__dot" aria-hidden="true" />
               <TooltipWrapper title={statusTooltip}>
                  <span className={`ann-quality-badge__status ${verified ? "ann-quality-badge__status--verified" : "ann-quality-badge__status--unverified"}`}>
                     {verified ? "✓" : "–"}
                  </span>
               </TooltipWrapper>
            </div>
         </div>
      );
   }, []);

   const renderEllipsisCell = useCallback((valueRaw: unknown) => {
      const value = valueRaw === undefined || valueRaw === null ? "-" : String(valueRaw);
      if (!value || value === "-") {
         return <span className="ann-cell-ellipsis">-</span>;
      }
      return (
         <TooltipWrapper title={value}>
            <span className="ann-cell-ellipsis">{value}</span>
         </TooltipWrapper>
      );
   }, []);

   const columns = useMemo(() => [
      {
         key: 'primaryAction',
         name: '',
         width: 44,
         minWidth: 44,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return null;
            return (
               <ANNAnnotationPrimaryActionCell
                  record={props.row?._record}
                  importingId={importingId}
                  downloadingId={downloadingId}
                  onShowNote={handleShowNote}
                  onImport={handleImport}
                  onOpenDocument={handleOpenDocument}
                  onDownloadDocument={handleDownloadDocument}
               />
            );
         }
      },
      {
         key: 'title',
         name: Localization.getText('Title') || 'Title',
         width: 220,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               return (
                  <div className="ann-history-label">
                     <span className="ann-history-label__arrow">└─</span>
                     <span className="ann-history-label__text">{Localization.getText("History") || "History"}</span>
                  </div>
               );
            }
            return renderEllipsisCell(props.row?.title || "-");
         }
      },
      {
         key: 'annotationType',
         name: Localization.getText('Annotation Type'),
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return <span>-</span>;
            return renderEllipsisCell(props.row?.annotationType || "-");
         }
      },
      ...(activeTab === "servitut" ? [{
         key: 'datoloebenr',
         name: Localization.getText('Date/serial number'),
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return <span>-</span>;
            return renderEllipsisCell(props.row?.datoloebenr || "-");
         }
      }] : []),
      ...(activeTab === "ejendom" ? [{
         key: 'bfeNumber',
         name: Localization.getText('BFE Number'),
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return <span>-</span>;
            return renderEllipsisCell(props.row?.bfeNumber || "-");
         }
      }] : []),
      {
         key: 'quality',
         name: Localization.getText('Status') || 'Status',
         width: 72,
         renderCell: (props: any) => {
            const annotation = props.row?._record as ANNAnnotationRecord | undefined;
            if (!annotation) return <span>-</span>;
            if (props.row?._isHistoryRow) {
               const entry = props.row?._historyEntry;
               if (!entry?.quality && !entry?.verified) return <span>-</span>;
               return renderQualityCell(entry?.quality, entry?.verified);
            }
            const displayQuality = annotation.quality;
            const isVerified = !!annotation.verified;
            const verifierInfo = isVerified && annotation.lastEditedByName
               ? `${Localization.getText("Verified by")}: ${annotation.lastEditedByName}${annotation.lastEditedAt ? `, ${new Date(annotation.lastEditedAt).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" })}` : ""}`
               : "";
            return renderQualityCell(displayQuality, isVerified, verifierInfo);
         }
      },
      {
         key: 'createdAt',
         name: Localization.getText('Last Edited') || 'Last edited',
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               return renderEllipsisCell(props.row?.createdAt || "-");
            }
            return renderEllipsisCell(props.row?.createdAt || "-");
         }
      },
      {
         key: 'createdByName',
         name: Localization.getText('Last Edited by') || 'Last edited by',
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               return renderEllipsisCell(props.row?.createdByName || "-");
            }
            return renderEllipsisCell(props.row?.createdByName || "-");
         }
      },
      {
         key: 'historyComment',
         name: Localization.getText('Comment') || 'Comment',
         width: 260,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) {
               const entry = props.row?._historyEntry;
               return renderEllipsisCell(entry?.comment || "-");
            }
            return renderEllipsisCell(props.row?.historyComment || "-");
         }
      },
      {
         key: 'actions',
         name: Localization.getText('Actions'),
         width: 200,
         renderCell: (props: any) => {
            if (props.row?._isHistoryRow) return null;
            const record = props.row?._record as ANNAnnotationRecord | undefined;
            const historyAvailable = (() => {
               if (!record) return false;
               const touchedAt = record.lastEditedAt || record.createdAt;
               let entries = [...(record.history || [])].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
               if (entries.length > 0 && touchedAt && entries[0].timestamp === touchedAt) {
                  entries = entries.slice(1);
               }
               return entries.length > 0;
            })();
            return (
               <ANNAnnotationActionsCell
                  record={props.row?._record}
                  importingId={importingId}
                  downloadingId={downloadingId}
                  deletingId={deletingId}
                  editingId={editingId}
                  canVerify={canVerify}
                  canDelete={canDelete}
                  canWrite={canWrite}
                  contextType={activeTab === "ejendom" ? AnnotationContextType.Ejendom : AnnotationContextType.Servitut}
                  onShowNote={handleShowNote}
                  onImport={handleImport}
                  onDownloadDocument={handleDownloadDocument}
                  onDelete={handleDelete}
                  onEditNote={handleEditNote}
                  onEditStedfaestelse={handleEditStedfaestelse}
                  onEditDocument={handleEditDocument}
                  onOpenServitutDetails={handleOpenServitutDetails}
                  showServitutDetailsButton={activeTab === "servitut"}
                  alwaysShowDownload={true}
                  onToggleHistory={handleToggleHistory}
                  historyAvailable={historyAvailable}
                  isHistoryOpen={historyAvailable && !!props.row?._record && expandedHistoryIds.has(props.row._record.id)}
                  hidePrimary={true}
               />
            );
         }
      }
   ], [
      activeTab,
      importingId,
      downloadingId,
      deletingId,
      editingId,
      canVerify,
      canDelete,
      canWrite,
      handleShowNote,
      handleImport,
      handleDownloadDocument,
      handleOpenDocument,
      handleDelete,
      handleEditNote,
      handleEditStedfaestelse,
      handleEditDocument,
      handleOpenServitutDetails,
      handleToggleHistory,
      renderQualityCell,
      expandedHistoryIds
   ]);

   type SortColumn = { columnKey: string; direction: 'ASC' | 'DESC' };
   const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);
   const sortedRows = useMemo(() => {
      if (!sortColumns || sortColumns.length === 0) return baseRows;
      const s = [...baseRows];
      s.sort((a: any, b: any) => {
         for (const sort of sortColumns) {
            const key = sort.columnKey;
            let av: any = a[key];
            let bv: any = b[key];
            if (key === 'createdAt') {
               av = a._createdAtSort;
               bv = b._createdAtSort;
            }
            if (av === bv) continue;
            const result = av < bv ? -1 : 1;
            return sort.direction === 'ASC' ? result : -result;
         }
         return 0;
      });
      return s;
   }, [baseRows, sortColumns]);
   const displayRows = useMemo(() => {
      const rows: any[] = [];
      sortedRows.forEach(row => {
         rows.push(row);
         if (expandedHistoryIds.has(row._record.id)) {
            let historyEntries = [...(row._record.history || [])].sort((a, b) => {
               return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
            });
            const touchedAt = row._record.lastEditedAt || row._record.createdAt;
            if (historyEntries.length > 0 && touchedAt && historyEntries[0].timestamp === touchedAt) {
               historyEntries = historyEntries.slice(1);
            }
            historyEntries.forEach((entry) => {
               rows.push({
                  _isHistoryRow: true,
                  _record: row._record,
                  _historyEntry: entry,
                  identifier: "",
                  annotationCount: "",
                  type: "",
                  annotationType: "",
                  datoloebenr: "",
                  bfeNumber: "",
                  quality: "",
                  createdAt: new Date(entry.timestamp).toLocaleString(Localization.getLocale()),
                  _createdAtSort: row._createdAtSort,
                  createdByName: entry.userName || entry.userId,
                  title: "",
                  isVerified: false,
               });
            });
         }
      });
      return rows;
   }, [sortedRows, expandedHistoryIds]);

   if (!hasAccessToFeature(Feature.AnnotationAuthUser)) {
      return (
         <AdvancedDragModal
            title={Localization.getText("Annotation Control Panel")}
            window={props.managedWindow}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ left: "5%", top: "5%", width: "90%", height: "90%" }}
            onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         >
            <ADModalBody>
               <div className="ann-unverified-accessDenied">
                  {Localization.getText("Access denied") || "Access denied"}
               </div>
            </ADModalBody>
         </AdvancedDragModal>
      );
   }

   return (
      <Fragment>
         <AdvancedDragModal
            title={Localization.getText("Annotation Control Panel")}
            window={props.managedWindow}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ left: "5%", top: "5%", width: "90%", height: "90%" }}
            onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         >
            <ADModalBody>
               <div className="ann-unverified-container">
                  <div className="ann-unverified-header">
                     <div className="ann-unverified-topRow">
                        <div className="ann-unverified-tabs">
                           <button
                              onClick={() => {
                                 setActiveTab("servitut");
                                 setDatoloebenrSearch("");
                                 setBfeSearch("");
                              }}
                              className={`ann-unverified-tab ${activeTab === "servitut" ? "ann-unverified-tab-active" : ""}`}
                           >
                              {Localization.getText("Servitut Annotations")}
                           </button>
                           <button
                              onClick={() => {
                                 setActiveTab("ejendom");
                                 setDatoloebenrSearch("");
                                 setBfeSearch("");
                              }}
                              className={`ann-unverified-tab ${activeTab === "ejendom" ? "ann-unverified-tab-active" : ""}`}
                           >
                              {Localization.getText("BFE Annotations")}
                           </button>
                        </div>
                        <button
                           onClick={handleRefresh}
                           disabled={loading}
                           className="ann-unverified-refreshBtn"
                        >
                           <MIBIcon icon="refresh" />
                           {Localization.getText("Refresh")}
                        </button>
                     </div>

                     <div className="ann-unverified-filtersRow">
                        {activeTab === "servitut" && (
                           <div className="ann-unverified-filterGroup">
                              <label className="ann-unverified-filterLabel">
                                 {Localization.getText("Search") || "Search"}:
                              </label>
                              <input
                                 type="text"
                                 placeholder={Localization.getText("Search by date/serial number")}
                                 value={datoloebenrSearch}
                                 onChange={(e) => setDatoloebenrSearch(e.target.value)}
                                 className="ann-unverified-filterInput"
                              />
                           </div>
                        )}
                        {activeTab === "ejendom" && (
                           <div className="ann-unverified-filterGroup">
                              <label className="ann-unverified-filterLabel">
                                 {Localization.getText("Search") || "Search"}:
                              </label>
                              <input
                                 type="text"
                                 placeholder={Localization.getText("Search by BFE number")}
                                 value={bfeSearch}
                                 onChange={(e) => setBfeSearch(e.target.value)}
                                 className="ann-unverified-filterInput"
                              />
                           </div>
                        )}
                        <label className="ann-unverified-toggle">
                           <input
                              type="checkbox"
                              checked={showOnlyUnverified}
                              onChange={(e) => setShowOnlyUnverified(e.target.checked)}
                           />
                           {Localization.getText("Show only unverified")}
                        </label>
                     </div>

                     <div className={`ann-unverified-geometryRow ${filterGeometry ? "ann-unverified-geometryRow--withPill" : ""}`}>
                        <div className="ann-unverified-geometryInner">
                           <label className="ann-unverified-filterLabel">
                              {Localization.getText("Filter by layer geometry") || "Filter by layer geometry"}:
                           </label>
                           <select
                              value={selectedFilterLayerId === "" ? "" : String(selectedFilterLayerId)}
                              onChange={(e) => setSelectedFilterLayerId(e.target.value === "" ? "" : parseInt(e.target.value))}
                              className="ann-unverified-geometrySelect"
                           >
                              <option value="">{Localization.getText("Select layer...")}</option>
                              {availableFilterLayers.map(item => (
                                 <option key={item.layer.layerId} value={item.layer.layerId}>
                                    {item.layer.datasetname || item.layer.filename || `Layer ${item.layer.layerId}`} ({item.typeLabel})
                                 </option>
                              ))}
                           </select>
                           <GlassButton
                              disabled={selectedFilterLayerId === "" || selectedFilterLayerId === null || selectedFilterLayerId === undefined}
                              onClick={handleApplyGeometryFilter}
                              className="ann-unverified-geometryBtn"
                           >
                              {Localization.getText("Apply filter")}
                           </GlassButton>
                           {filterGeometry && (
                              <GlassButton
                                 onClick={handleClearGeometryFilter}
                                 className="ann-unverified-geometryClearBtn"
                              >
                                 {Localization.getText("Clear filter")}
                              </GlassButton>
                           )}
                        </div>
                     </div>

                     {filterGeometry && (
                        <div className="ann-unverified-activeFilterPill">
                           <span>✓</span>
                           <span>{Localization.getText("Filtering by layer geometry")}</span>
                        </div>
                     )}
                  </div>

                  {error && displayRows.length === 0 && (
                     <div className="ann-unverified-error ann-unverified-errorBox">
                        {error}
                     </div>
                  )}

                  {loading && displayRows.length === 0 && (
                     <div className="ann-unverified-loading ann-unverified-loadingBox">
                        {Localization.getText("Loading...") || "Loading..."}
                     </div>
                  )}

                  {(!loading || displayRows.length > 0) && !error && (
                     <>
                        {displayRows.length === 0 ? (
                           <div className="ann-unverified-empty ann-unverified-emptyBox">
                              {showOnlyUnverified
                                 ? Localization.getText("No unverified annotations found")
                                 : Localization.getText("No annotations found")}
                           </div>
                        ) : (
                           <div className="ann-unverified-gridWrapper">
                              <ReactDataGrid
                                 className='rdg-light ann-table-no-scroll'
                                 columns={columns}
                                 rows={displayRows}
                                 sortColumns={sortColumns}
                                 onSortColumnsChange={setSortColumns as any}
                                 defaultColumnOptions={{ resizable: true, sortable: true }}
                                 rowHeight={35}
                                 rowClass={(row: any) => row?._isHistoryRow ? "ann-history-row" : ""}
                                 enableVirtualization={true}
                              />
                           </div>
                        )}
                     </>
                  )}
               </div>
            </ADModalBody>
         </AdvancedDragModal>

         {editNoteModalOpen && annotationToEdit && organizationId && (
            <ANNCreateNote
               open={editNoteModalOpen}
               contextType={activeTab === "ejendom" ? AnnotationContextType.Ejendom : AnnotationContextType.Servitut}
               documentId={activeTab === "servitut" ? annotationToEdit.documentId : undefined}
               bfeNumber={activeTab === "ejendom" ? annotationToEdit.bfeNumber : undefined}
               datoloebenr={activeTab === "servitut" ? annotationToEdit.datoloebenr : undefined}
               editAnnotation={annotationToEdit}
               oldAnnotationId={annotationToEdit.id}
               onClose={() => {
                  setEditNoteModalOpen(false);
                  setAnnotationToEdit(null);
               }}
               onCreated={async () => {
                  setEditNoteModalOpen(false);
                  setAnnotationToEdit(null);
               }}
            />
         )}
         {editDocumentModalOpen && documentToEdit && organizationId && (
            <ANNCreateDocument
               open={editDocumentModalOpen}
               contextType={activeTab === "ejendom" ? AnnotationContextType.Ejendom : AnnotationContextType.Servitut}
               documentId={activeTab === "servitut" ? documentToEdit.documentId : undefined}
               bfeNumber={activeTab === "ejendom" ? documentToEdit.bfeNumber : undefined}
               datoloebenr={activeTab === "servitut" ? documentToEdit.datoloebenr : undefined}
               editAnnotation={documentToEdit}
               oldAnnotationId={documentToEdit.id}
               onClose={() => {
                  setEditDocumentModalOpen(false);
                  setDocumentToEdit(null);
               }}
               onCreated={async () => {
                  setEditDocumentModalOpen(false);
                  setDocumentToEdit(null);
               }}
            />
         )}
      </Fragment>
   );
}
